/**
 * PlanListView — Saved flight plans list with CRUD operations.
 */
import type { FlightPlanSummary } from '@efb/shared-core';

interface Props {
  plans: FlightPlanSummary[];
  onSelect: (planId: string) => void;
  onDelete: (planId: string) => void;
  onNew: () => void;
}

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

const STATUS_BADGES: Record<string, string> = {
  draft: '📝', filed: '📤', active: '✈️', completed: '✅', cancelled: '❌',
};

export function PlanListView({ plans, onSelect, onDelete, onNew }: Props) {
  return (
    <div className="panel plan-list-panel" role="region" aria-label="Flight Plans">
      <div className="plan-list-header">
        <h2>Flight Plans</h2>
        <button className="fp-btn fp-btn-primary" onClick={onNew}>+ New Plan</button>
      </div>

      {plans.length === 0 ? (
        <p className="empty-state">No flight plans yet. Create one to get started!</p>
      ) : (
        <ul className="plan-list" role="list">
          {plans.map(plan => (
            <li key={plan.id} className="plan-list-item" role="listitem">
              <div className="plan-item-main"
                onClick={() => onSelect(plan.id)}
                onKeyDown={e => e.key === 'Enter' && onSelect(plan.id)}
                role="button" tabIndex={0}>
                <div className="plan-item-header">
                  <span className="plan-badge">{STATUS_BADGES[plan.status] ?? '📝'}</span>
                  <strong>{plan.name}</strong>
                  <span className="plan-rules">{plan.flightRules}</span>
                </div>
                <div className="plan-item-route">
                  {plan.departure} → {plan.destination}
                </div>
                <div className="plan-item-meta">
                  <span>{plan.totalDistanceNm.toFixed(0)} nm</span>
                  <span>{formatDate(plan.updatedAt)}</span>
                </div>
              </div>
              <button className="fp-btn fp-btn-danger fp-btn-sm"
                onClick={e => { e.stopPropagation(); onDelete(plan.id); }}
                aria-label={`Delete ${plan.name}`}>
                🗑️
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
